/**
 * Plain WebSocket module — no React dependency.
 * Connects to the occum.net API WebSocket and dispatches events to the Zustand store.
 */
import { useStore } from "../store";

let ws: WebSocket | null = null;
let retries = 0;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let pingInterval: ReturnType<typeof setInterval> | null = null;
let active = false;

function resolveWsUrl(): string {
  if (process.env.NEXT_PUBLIC_WS_URL) return process.env.NEXT_PUBLIC_WS_URL;
  const proto = window.location.protocol === "https:" ? "wss" : "ws";
  const host = window.location.hostname;
  const baseHost = host.replace(/^www\./, "");
  const apiHost = baseHost === "localhost" || baseHost === "127.0.0.1"
    ? `${baseHost}:3001`
    : `api.${baseHost}`;
  return `${proto}://${apiHost}/v1/ws`;
}

function connect(token: string) {
  if (!active) return;

  const store = useStore.getState();
  store.setWsStatus("connecting");

  const wsUrl = resolveWsUrl();
  const socket = new WebSocket(wsUrl);
  ws = socket;

  socket.onopen = () => {
    const authMsg: any = { type: "auth", token };
    const lastEventId = useStore.getState().ui.lastEventId;
    if (lastEventId) {
      authMsg.lastEventId = lastEventId;
    }
    socket.send(JSON.stringify(authMsg));
  };

  socket.onmessage = (e) => {
    try {
      const data = JSON.parse(e.data);

      if (data.type === "auth_ok") {
        retries = 0;
        useStore.getState().setWsStatus("authenticated");
        // Start keepalive pings
        if (pingInterval) clearInterval(pingInterval);
        pingInterval = setInterval(() => {
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: "ping" }));
          }
        }, 25000);
        return;
      }

      if (data.type === "auth_error") {
        socket.close();
        return;
      }

      if (data.type === "pong") return;

      // Route terminal messages to terminal listeners
      if (typeof data.type === "string" && data.type.startsWith("terminal.")) {
        for (const cb of terminalListeners) cb(data);
        return;
      }

      // Dispatch to store
      dispatchEvent(data);
    } catch {
      // ignore non-JSON
    }
  };

  socket.onerror = () => {
    // onclose will handle reconnect
  };

  socket.onclose = () => {
    ws = null;
    if (pingInterval) {
      clearInterval(pingInterval);
      pingInterval = null;
    }
    if (!active) return;

    useStore.getState().setWsStatus("disconnected");
    const baseDelay = Math.min(1000 * Math.pow(2, retries++), 30000);
    const jitter = baseDelay * 0.5 * Math.random();
    reconnectTimer = setTimeout(() => connect(token), baseDelay + jitter);
  };
}

function dispatchEvent(data: any) {
  const store = useStore.getState();

  if (data.type === "event" || data.eventType) {
    store.appendMessage(data);

    if (data.id && data.channelId) {
      // If the user is currently viewing this channel, mark read immediately
      // to prevent the "New messages" divider from flashing
      if (data.channelId === store.ui.selectedChannelId) {
        store.markChannelRead(data.channelId, data.id);
      }
      store.onUnreadEvent({ channelId: data.channelId, id: data.id, eventType: data.eventType });
      store.setLastEventId(data.id);
    }

    if (data.eventType === "work") {
      store.initWorkFromEvents([data], data.channelId);
    }
  }

  if (data.type === "work_update") {
    store.onWorkUpdate(data);
  }

  if (data.type === "work_complete") {
    store.onWorkComplete(data);
    if (data.channelId) {
      store.onUnreadWorkComplete(data.channelId);
    }
  }

  if (data.type === "read_cursor_update" && data.channelId && data.userId && data.eventId) {
    const currentUserId = store.auth.user?.id;
    store.onReadCursorUpdate(data.channelId, data.userId, data.eventId, currentUserId);
  }
}

// ─── Terminal message pub/sub ─────────────────────────────────────────────────
// Terminal messages (terminal.*) are dispatched to registered listeners rather
// than the Zustand store. The terminal page subscribes/unsubscribes on mount.

type TerminalListener = (msg: Record<string, unknown>) => void;
const terminalListeners = new Set<TerminalListener>();

/** Subscribe to terminal WS messages. Returns unsubscribe function. */
export function onTerminalMessage(cb: TerminalListener): () => void {
  terminalListeners.add(cb);
  return () => { terminalListeners.delete(cb); };
}

/** Send a message through the WebSocket. */
export function sendWsMessage(msg: object): void {
  if (ws && ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify(msg));
  }
}

/**
 * Start the WebSocket connection. Call when authenticated.
 */
export function connectWebSocket(token: string) {
  if (active) disconnectWebSocket();
  active = true;
  retries = 0;
  connect(token);
}

/**
 * Tear down the WebSocket connection.
 */
export function disconnectWebSocket() {
  active = false;
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (pingInterval) {
    clearInterval(pingInterval);
    pingInterval = null;
  }
  if (ws) {
    ws.close();
    ws = null;
  }
}
